import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { RequestFailed } from "src/components/RequestFailed";
import { format } from "date-fns";
import { Button, Icon } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { FaArrowRight, FaRegMoneyBillAlt, FaArrowLeft } from "react-icons/fa";
import { GrLocation } from "react-icons/gr";
import {
	formatAsCurrency,
	formatContractJobAdDuration,
	formatEmploymentType,
} from "src/utlis/helpers";
import { RiBuildingLine } from "react-icons/ri";
import { MdOutlineBusinessCenter } from "react-icons/md";
import { Tags } from "lucide-react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { RxCounterClockwiseClock } from "react-icons/rx";
import { logEvent } from "src/utlis/analytics";
import { GET_JOB_AD } from "src/features/jobs/graphql";
import { Loading } from "@/components/Loading";
import { useEffect } from "react";

export default function LaborHackJobDetails() {
	const { id } = useParams<{ id: string }>();

	const navigate = useNavigate();

	useEffect(() => {
		if (!id) {
			navigate("/opportunities/laborhack");
		}
	}, [id, navigate]);

	const {
		loading: jobLoading,
		error: jobError,
		data: jobData,
	} = useQuery(GET_JOB_AD, {
		fetchPolicy: "network-only",
		variables: {
			id: id!,
		},
		skip: !id,
	});

	const job = jobData?.getEmployerJobAd;

	if (jobError) {
		return <RequestFailed text={jobError.message} />;
	}

	return (
		<>
			<Helmet>
				<title>
					{job?.title ? `${job.title} - LaborHack Jobs` : "Job - LaborHack"}
				</title>
			</Helmet>
			<div>
				<main>
					{jobLoading ? (
						<div className="flex justify-center items-center h-screen">
							<Loading />
						</div>
					) : job ? (
						<section className="px-3 lg:px-[75px] w-full">
							<div className="w-full border-[#ACACAC]">
								<div className="w-full">
									<div className="mb-6 lg:mb-8">
										<Button
											leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
											variant="link"
											colorScheme="prussianBlue"
											fontSize={{
												base: "14px",
												lg: "16px",
											}}
											onClick={() => navigate("/opportunities/laborhack")}
										>
											Back to LaborHack jobs
										</Button>
									</div>
									<div className="border-[#ACACAC] border-b-[1px] pb-6 lg:pb-8 flex flex-col lg:flex-row gap-4 lg:justify-between lg:items-end">
										<div className="w-full flex flex-col gap-4 lg:gap-6">
											<div className="w-[85%] lg:w-[55%]">
												<h3 className="text-[#0E212D] text-[18px] lg:text-[23px] font-medium">
													{job.title}
												</h3>
											</div>
											<div className="flex flex-col gap-2">
												<p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
													Date posted:{" "}
													<span className="text-[#000000]">
														{format(new Date(job.createdAt), "PPP")}
													</span>
												</p>
											</div>
											{job.applicationDeadline && (
												<div className="flex flex-col gap-2">
													<p className="text-[#6E6E6E] text-[14px] lg:text-[18px] font-normal">
														Application deadline:{" "}
														<span className="text-[#883227]">
															{format(new Date(job.applicationDeadline), "PPP")}
														</span>
													</p>
												</div>
											)}
										</div>
										<div className="flex flex-col lg:flex-row gap-3">
											<Button
												bgColor={
													job.hasApplied && !job.canApply
														? "#FBE2DE"
														: "#ED6E5C"
												}
												color={
													job.hasApplied && !job.canApply
														? "#F08677"
														: "#FEFEFE"
												}
												h={{
													base: "40px",
													lg: "57px",
												}}
												fontSize={{
													base: "16px",
													lg: "20px",
												}}
												rightIcon={
													<Icon
														as={
															job.hasApplied && !job.canApply
																? IoCheckmarkCircle
																: FaArrowRight
														}
														fill={
															job.hasApplied && !job.canApply
																? "#F08677"
																: "#FEFEFE"
														}
														w={{
															base: 4,
															lg: 5,
														}}
														h={{
															base: 4,
															lg: 5,
														}}
														ml={0}
														fontWeight={"500"}
													/>
												}
												isDisabled={job.hasApplied && !job.canApply}
												onClick={() => {
													logEvent("job_application_started", {
														job_id: job.id,
														job_title: job.title,
													});

													navigate(`/opportunities/laborhack/${job.id}/apply`);
												}}
											>
												{job.hasApplied && !job.canApply ? "Applied" : "Apply"}
											</Button>
										</div>
									</div>
									<div className="border-[#ACACAC] border-b-[1px] py-6 lg:py-8">
										<div
											className="markdown"
											dangerouslySetInnerHTML={{
												__html: job.description,
											}}
										/>
									</div>
									<div className="grid grid-cols-1 lg:grid-cols-[3fr,5fr] gap-6 lg:gap-8 py-6 lg:py-8">
										<div className="flex items-center gap-4">
											<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
												<Icon
													as={GrLocation}
													w={{
														base: "20px",
														lg: "20px",
													}}
													h={{
														base: "20px",
														lg: "20px",
													}}
													color={"#212121"}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
													Location
												</p>
												<p className="text-[#212121] text-[16px] lg:text-[20px]">
													{`${job.location.area}, ${job.location.state}, ${job.location.country}`}
												</p>
											</div>
										</div>

										<div className="flex items-center gap-4">
											<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
												<Icon
													as={MdOutlineBusinessCenter}
													w={{
														base: "20px",
														lg: "20px",
													}}
													h={{
														base: "20px",
														lg: "20px",
													}}
													color={"#212121"}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
													Employment type
												</p>
												<p className="text-[#212121] text-[16px] lg:text-[20px]">
													{`${formatEmploymentType(job.employmentType)}`}
												</p>
											</div>
										</div>

										{job.duration && (
											<div className="flex items-center gap-4">
												<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
													<Icon
														as={RxCounterClockwiseClock}
														w={{
															base: "20px",
															lg: "20px",
														}}
														h={{
															base: "20px",
															lg: "20px",
														}}
														color={"#212121"}
													/>
												</div>
												<div className="flex flex-col gap-2">
													<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
														Duration
													</p>
													<p className="text-[#212121] text-[16px] lg:text-[20px]">
														{formatContractJobAdDuration(job.duration)}
													</p>
												</div>
											</div>
										)}

										{job.salaryDetails && (
											<div className="flex items-center gap-4">
												<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
													<Icon
														as={FaRegMoneyBillAlt}
														w={{
															base: "20px",
															lg: "20px",
														}}
														h={{
															base: "20px",
															lg: "20px",
														}}
														color={"#212121"}
													/>
												</div>
												<div className="flex flex-col gap-2">
													<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
														{`Salary ${job.salaryDetails.cap ? "range" : ""}`}
													</p>
													<p className="text-[#212121] text-[16px] lg:text-[20px]">
														{formatAsCurrency(
															job.salaryDetails.starting,
															job.salaryDetails.currencyCode
														)}{" "}
														{job.salaryDetails.cap
															? `- ${formatAsCurrency(
																	job.salaryDetails.cap,
																	job.salaryDetails.currencyCode
															  )}`
															: ""}
													</p>
												</div>
											</div>
										)}

										<div className="flex items-center gap-4">
											<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
												<Icon
													as={RiBuildingLine}
													w={{
														base: "20px",
														lg: "20px",
													}}
													h={{
														base: "20px",
														lg: "20px",
													}}
													color={"#212121"}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
													Employer
												</p>
												<p className="text-[#212121] text-[16px] lg:text-[20px]">
													{job.companyName}
												</p>
											</div>
										</div>

										{job.tags && job.tags.length > 0 && (
											<div className="flex items-start gap-4">
												<div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
													<Icon
														as={Tags}
														w={{
															base: "20px",
															lg: "20px",
														}}
														h={{
															base: "20px",
															lg: "20px",
														}}
														color={"#212121"}
													/>
												</div>
												<div className="flex flex-col gap-2">
													<p className="text-[#6E6E6E] text-[14px] lg:text-[16px]">
														Tags
													</p>
													<p className="text-[#212121] text-[16px] lg:text-[20px]">
														{job.tags.map((tag) => tag.name).join(", ")}
													</p>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						</section>
					) : (
						<div className="flex items-center justify-center h-full p-4 lg:p-8 border-[1px] border-[#ACACAC]">
							<p className="text-[#0E212D] text-lg lg:text-xl">Job not found</p>
						</div>
					)}
				</main>
			</div>
		</>
	);
}
